import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { UserSquare, Building2 } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function VehicleAssignmentDialog({ vehicle, canEdit, onClose, onSaved }) {
  const open = !!vehicle;
  const [owners, setOwners] = useState([]);
  const [form, setForm] = useState({ institute_id: "", branch: "", department: "", location: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicle) return;
    setLoading(true);
    api.get("/admin/vehicles/assignable-owners")
      .then(({ data }) => setOwners(data || []))
      .catch(() => setOwners([]))
      .finally(() => setLoading(false));
    setForm({
      institute_id: vehicle.institute_id || "",
      branch: vehicle.branch || "",
      department: vehicle.department || "",
      location: vehicle.location || "",
    });
  }, [vehicle]);

  const currentOwner = useMemo(() => {
    if (!vehicle) return "";
    if (vehicle.institute_name) return vehicle.institute_name;
    return vehicle.institute_id ? "(unknown owner)" : "Not assigned";
  }, [vehicle]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/vehicles/${vehicle.id}/assignment`, form);
      toast.success(form.institute_id ? "Vehicle assigned" : "Vehicle unassigned");
      onSaved && onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to save assignment");
    } finally { setSaving(false); }
  };

  const unassign = async () => {
    setForm({ institute_id: "", branch: "", department: "", location: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fbf8f1] max-w-2xl" data-testid="veh-assignment-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-3">
            <UserSquare size={22} className="text-[#234333]" />
            Assign Vehicle — {vehicle?.registration_number}
          </DialogTitle>
          <DialogDescription>
            {vehicle?.make} {vehicle?.model_name} · Set or change which trust / institute owns this vehicle, and the specific branch, department or location it operates from.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-white border border-[#e5e0d8] rounded-sm p-4 flex items-center gap-3" data-testid="veh-assignment-current">
          <div className="w-10 h-10 rounded-sm bg-[#f5f1e8] flex items-center justify-center"><Building2 size={16} className="text-[#234333]" /></div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Current owner</div>
            <div className="text-sm text-[#1c1a17] font-semibold">{currentOwner}</div>
            {(vehicle?.branch || vehicle?.department || vehicle?.location) && (
              <div className="text-[11px] text-[#8a8478]">{[vehicle.branch, vehicle.department, vehicle.location].filter(Boolean).join(" · ")}</div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <Field label="Assigned Owner (Trust or Institute)" full>
            <select data-testid="veh-assign-owner" className={inputCls} value={form.institute_id} onChange={(e) => set("institute_id", e.target.value)} disabled={!canEdit || loading}>
              <option value="">— Not assigned —</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.type === "trust" ? `★ ${o.name} (Trust)` : o.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Branch">
            <input data-testid="veh-assign-branch" className={inputCls} value={form.branch} onChange={(e) => set("branch", e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Department">
            <input data-testid="veh-assign-dept" className={inputCls} value={form.department} onChange={(e) => set("department", e.target.value)} disabled={!canEdit} />
          </Field>
          <Field label="Location" full>
            <input data-testid="veh-assign-location" className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} disabled={!canEdit} />
          </Field>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between w-full">
          {canEdit && (form.institute_id || form.branch || form.department || form.location) ? (
            <button type="button" onClick={unassign} data-testid="veh-assign-clear" className="text-sm px-4 py-2 rounded-sm border border-red-300 text-red-700 hover:bg-red-50">Clear assignment</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            {canEdit && (
              <button type="button" onClick={save} disabled={saving} data-testid="veh-assign-save" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">
                {saving ? "Saving…" : "Save Assignment"}
              </button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
